import {
  $email,
  $fullname,
  $lang,
  $name,
  $tenantConfiguration,
  $userLastLogin,
  $userTimezone,
  ApiError,
  cleanShouldShowLastLogin,
  getTag,
  useApiClient,
  useAsyncCache,
  useAuth,
  useTranslation,
} from '@msdk/common'
import { addQueryParams } from '@msdk/common/src/utils/url-utils'
import { readTeantConfiguration, readUserData, TenantPath } from '@msdk/definitions'
import { stringFormat, useComponentModal } from '@msdk/forms'
import {
  Avatar,
  Box,
  ButtonPrimary,
  ButtonSecondary,
  COLORS,
  createComponentWrapperWithRef,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  uiTokens,
} from '@msdk/ui'
import { styled } from '@msdk/ui'
import {
  BadgeOutlined,
  ChevronRight,
  KeyboardArrowDownOutlined,
  LocationCity,
  Logout,
  Password,
  PersonOutline,
} from '@mui/icons-material'
import { triggerSessionEnding } from 'hooks'
import { MouseEvent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { DefaultClinicSelector } from '../DefaultClinic'
import { UserRoles } from './UserRoles'

const MuiAvatarInfo = styled(ButtonPrimary)`
  display: flex;
  border-radius: 8px;
  padding: 4px 8px 4px 16px;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  background-color: transparent;
  height: unset;
  margin-top: 4px;
  margin-bottom: 4px;

  &:hover {
    background-color: ${COLORS.blue900};
    box-shadow: unset;
  }
`

const NovaAvatarInfo = styled(ButtonPrimary)`
  display: flex;
  border-radius: 8px;
  padding: 4px 8px 4px 16px;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  background-color: transparent;
  height: unset;
  margin-top: 4px;
  margin-bottom: 4px;

  &:hover {
    background-color: transparent;
    color: white;
    box-shadow: unset;
  }
`

export const AvatarInfo = createComponentWrapperWithRef(
  {
    mui: MuiAvatarInfo,
    nova: NovaAvatarInfo,
  },
  'AvatarInfo',
)

const StyledMenu = styled(Menu)`
  top: 4px;

  .MuiPaper-root {
    width: 320px;
    max-width: 320px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    border-radius: 8px;
  }

  li,
  ul {
    max-width: 320px;
  }

  ul {
    padding-top: 0;
    padding-left: 0;
    padding-right: 0;
  }
`

const StyledNovaBox = styled(StyledMenu)`
  li {
    margin-left: ${uiTokens.spacing.padding.medium}px;
    margin-right: ${uiTokens.spacing.padding.medium}px;
  }
`

export const MainMenu = createComponentWrapperWithRef(
  {
    mui: StyledMenu,
    nova: StyledNovaBox,
  },
  'MainMenu',
)

const UserMenuItem = styled('li')`
  background-color: rgba(227, 241, 255, 1);
  width: 320px;
  padding: 16px;
  margin-left: 0 !important;
  margin-right: 0 !important;
  .MuiTypography-body2 {
    color: ${COLORS.text.secondary};
  }
`
const StyledRolesMenuItem = styled(MenuItem)`
  margin-top: 16px;
  padding-top: 8px;
  padding-bottom: 8px;
  display: flex;
  justify-content: space-between;
`

const StyledRolesLabel = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: none;
  margin: none;
`

const StyledLogoutMenuItem = styled(MenuItem)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 320px;
  margin-left: 0 !important;
  margin-right: 0 !important;
  :hover {
    background-color: inherit;
    cursor: default;
  }
`

const StyledLogoutButton = styled(ButtonSecondary)`
  gap: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`

const UserProfileLink = styled(Link)`
  text-decoration: none;
  color: ${COLORS.text.primary};
  margin-bottom: 10px;
  padding-top: 8px;
  padding-bottom: 8px;
`

const LeftMenuItemIcon = styled(ListItemIcon)``

const RightMenuItemIcon = styled(ListItemIcon)`
  justify-content: end;
  padding: 0;
`

const StyledAvatar = styled(Avatar)`
  background-color: ${COLORS.blue100};
  color: ${COLORS.text.primary};
`

const StyledTypography = styled(Typography)`
  font-size: 20px;
  line-height: 20px;
  color: ${COLORS.text.primary};
`

const UserTypography = styled(Typography)`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`

function IsTechServCustomizationEnabled(): boolean {
  const tenantPath = new TenantPath()
  return tenantPath.IsCustomizationEnabled('TECH_SERV')
}

export function SettingsMenu() {
  const [anchorUsernameEl, setUsernameAnchorEl] = useState<HTMLElement | null>(null)
  const [popoverUsernameText, setUsernamePopoverText] = useState<string>('')
  const [anchorEmailEl, setEmailAnchorEl] = useState<HTMLElement | null>(null)
  const [popoverEmailText, setEmailPopoverText] = useState<string>('')
  const [techServCustomizationEnabled] = useState<boolean>(IsTechServCustomizationEnabled())
  const [actionAfterClose, setActionAfterClose] = useState<() => void>()
  const handleUsernameMouseEnter = (event: MouseEvent<HTMLElement>, text: string) => {
    const target = event.currentTarget
    if (target.scrollWidth > target.clientWidth) {
      setUsernamePopoverText(text)
      setUsernameAnchorEl(target)
    }
  }
  const handleUsernameMouseLeave = () => {
    setUsernameAnchorEl(null)
    setUsernamePopoverText('')
  }
  const handleEmailMouseEnter = (event: MouseEvent<HTMLElement>, text: string) => {
    const target = event.currentTarget
    if (target.scrollWidth > target.clientWidth) {
      setEmailPopoverText(text)
      setEmailAnchorEl(target)
    }
  }
  const handleEmailMouseLeave = () => {
    setEmailAnchorEl(null)
    setEmailPopoverText('')
  }

  const t = useTranslation()
  const client = useApiClient()
  const cache = useAsyncCache()
  const modal = useComponentModal()

  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const [open, setOpen] = useState<boolean>(false)
  const { onLogout, removeUser } = useAuth()
  const userName = useRecoilValue($name)

  const [userRolesMenuOpen, setUserRolesMenu] = useState<boolean>(false)

  const userFullName = useRecoilValue($fullname)
  const [isUserFromExternalProvider, setIsUserFromExternalProvider] = useState<boolean>(false)

  const email = useRecoilValue($email)

  const userLastLogin = useRecoilValue($userLastLogin)
  const lastLoginEnabled = useRecoilValue($tenantConfiguration)?.lastLoginEnabled ?? false
  const userTimezone = useRecoilValue($userTimezone)
  const lang = useRecoilValue($lang) || 'en-US'

  const formattedLastLogin = userLastLogin
    ? new Date(userLastLogin).toLocaleString(lang, {
        timeZone: userTimezone,
        day: '2-digit',
        year: 'numeric',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  const lastLoginMsg = formattedLastLogin
    ? stringFormat(t('LBL_LAST_LOGIN', 'Last login: {0}'), formattedLastLogin)
    : null

  const handleMenuToggle = (event?: MouseEvent<HTMLButtonElement>) => {
    if (open) {
      setAnchor(null)
      setOpen(false)
      setUserRolesMenu(false)
    } else if (event && event.currentTarget) {
      setAnchor(event.currentTarget)
      setOpen(true)
    }
  }

  function getUserEmailDomain(userEmail: string): string {
    try {
      return userEmail ? userEmail.split('@')[1] : ''
    } catch (error) {
      return ''
    }
  }

  async function isUserMailFromExternalProvider(userEmail: string): Promise<boolean> {
    const tenantExternalProviders = readTeantConfiguration().externalIdentityProviders
    if (!tenantExternalProviders) return false
    const tenantId = readUserData().tenant
    const domain: string = getUserEmailDomain(userEmail)

    if (domain) {
      const response = await cache.getOrCreate(`EXTIDP4DOMAIN_${tenantId}_${domain}`, async () => {
        try {
          const res = await client.read<boolean>(
            `IDP/api/ExternalIdentityProvider/IsDomainFromExternalProvider?domain=${domain}&tenantId=${tenantId}`,
          )
          return res
        } catch (e) {
          console.error('[isUserMailFromExternalProvider] - error calling api', e)
          return false
        }
      })

      if (response instanceof ApiError) return false
      else return response as boolean
    } else return false
  }

  useEffect(() => {
    const fetch = async () => {
      if (email) {
        const result = await isUserMailFromExternalProvider(email)
        setIsUserFromExternalProvider(result)
      } else setIsUserFromExternalProvider(() => false)
    }
    fetch().catch(console.error)
  }, [])

  function handleSetDefaultClinicClick(): void {
    setActionAfterClose(() => () => {
      modal
        .createModal(<DefaultClinicSelector />, () => {}, {
          preventClickOutside: false,
          preventEscapeFromClosing: false,
        })
        .open()
    })
    setOpen(false)
  }

  /**
   * A method used to get the initials of a user name.
   * @param input A string representation of the user name.
   * @returns The initials of the user name.
   */
  const getInitials = (input: string): string => {
    const items = input.split(' ')

    const firstLetter = items[0].charAt(0).toUpperCase()

    const lastLetter = items.length > 1 ? items[items.length - 1].charAt(0).toUpperCase() : ''

    const initials = firstLetter + lastLetter

    return initials
  }

  const onIdpNavigation = async (event: MouseEvent<HTMLAnchorElement>, targetUrl: string) => {
    event.preventDefault()
    await removeUser()
    window.location.href = targetUrl
  }

  const handleLogout = () => {
    triggerSessionEnding()
    onLogout({
      extraQueryParams: {
        idle: false,
      },
    })
    cleanShouldShowLastLogin()
  }

  return (
    <>
      <AvatarInfo onClick={handleMenuToggle} data-test-id={getTag('shell', 'header', 'avatar', 'button')}>
        {userFullName && (
          <StyledAvatar>
            <StyledTypography>{getInitials(userFullName)}</StyledTypography>
          </StyledAvatar>
        )}
        {!userFullName && (
          <StyledAvatar>
            <StyledTypography>{getInitials(userName)}</StyledTypography>
          </StyledAvatar>
        )}
        <KeyboardArrowDownOutlined />
      </AvatarInfo>
      <MainMenu
        anchorEl={anchor}
        open={open}
        onClose={() => handleMenuToggle()}
        TransitionProps={{
          onExited: () => {
            if (!actionAfterClose) return
            setActionAfterClose(undefined)
            actionAfterClose()
          },
        }}
      >
        <UserMenuItem>
          <ListItemText>
            <Tooltip open={Boolean(anchorUsernameEl)} onClose={handleUsernameMouseLeave} title={popoverUsernameText}>
              <UserTypography
                onMouseEnter={(event) =>
                  handleUsernameMouseEnter(event, userFullName || t('CMPSR_MENU_UNKNOWN_USER', 'Unknown user'))
                }
                onMouseLeave={handleUsernameMouseLeave}
                variant="body1"
              >{`${userFullName || t('CMPSR_MENU_UNKNOWN_USER', 'Unknown user')}`}</UserTypography>
            </Tooltip>
            {email && (
              <Tooltip open={Boolean(anchorEmailEl)} onClose={handleEmailMouseLeave} title={popoverEmailText}>
                <UserTypography
                  mt={0.5}
                  onMouseEnter={(event) => handleEmailMouseEnter(event, email)}
                  onMouseLeave={handleEmailMouseLeave}
                  variant="body2"
                >{`${email}`}</UserTypography>
              </Tooltip>
            )}
            {lastLoginEnabled && lastLoginMsg && (
              <UserTypography mt={0.5} variant="body2">
                {lastLoginMsg}
              </UserTypography>
            )}
          </ListItemText>
        </UserMenuItem>
        {!userRolesMenuOpen && (
          <Box>
            <StyledRolesMenuItem
              className="roles-menu-item"
              onClick={() => setUserRolesMenu(true)}
              data-test-id={getTag('shell', 'user_roles', 'USER_ROLES', 'button')}
            >
              <StyledRolesLabel>
                <LeftMenuItemIcon>
                  <BadgeOutlined />
                </LeftMenuItemIcon>
                {t('LBL_MY_ROLES', 'My Roles')}
              </StyledRolesLabel>
              <RightMenuItemIcon>
                <ChevronRight />
              </RightMenuItemIcon>
            </StyledRolesMenuItem>

            <UserProfileLink
              onClick={(e) =>
                onIdpNavigation(
                  e,
                  `${window.__APP_CONFIG__.AUTHORITY}/account/profile?returnUrl=${encodeURIComponent(
                    addQueryParams(location.href, 'profile_edit', 'true'),
                  )}`,
                )
              }
              to={`${window.__APP_CONFIG__.AUTHORITY}/account/profile?returnUrl=${encodeURIComponent(
                addQueryParams(location.href, 'profile_edit', 'true'),
              )}`}
            >
              <MenuItem data-test-id={getTag('shell', 'header', 'user_profile_details', 'link')}>
                <LeftMenuItemIcon>
                  <PersonOutline />
                </LeftMenuItemIcon>
                {t('LBL_USER_PROFILE_DETAILS', 'Profile')}
              </MenuItem>
            </UserProfileLink>

            <UserProfileLink
              onClick={(e) =>
                onIdpNavigation(
                  e,
                  `${window.__APP_CONFIG__.AUTHORITY}/account/changepassword?returnUrl=${encodeURIComponent(
                    addQueryParams(location.href, 'profile_edit', 'true'),
                  )}`,
                )
              }
              to={`${window.__APP_CONFIG__.AUTHORITY}/account/changepassword?returnUrl=${encodeURIComponent(
                addQueryParams(location.href, 'profile_edit', 'true'),
              )}`}
            >
              {!isUserFromExternalProvider && (
                <MenuItem data-test-id={getTag('shell', 'header', 'user_profile_password_change', 'link')}>
                  <LeftMenuItemIcon>
                    <Password />
                  </LeftMenuItemIcon>
                  {t('LBL_USER_PROFILE_CHANGE_PASSWORD', 'Change password')}
                </MenuItem>
              )}
            </UserProfileLink>

            <MenuItem
              onClick={handleSetDefaultClinicClick}
              data-test-id={getTag('shell', 'header', 'user_profile_default_clinic', 'button')}
            >
              <LeftMenuItemIcon>
                <LocationCity />
              </LeftMenuItemIcon>
              {techServCustomizationEnabled
                ? t('CMPSR_SET_DEFAULT_REGION', 'Set default region')
                : t('CMPSR_SET_DEFAULT_CLINIC', 'Set default clinic')}
            </MenuItem>

            <Divider />

            <StyledLogoutMenuItem>
              <StyledLogoutButton
                onClick={() => handleLogout()}
                data-test-id={getTag('shell', 'header_avatar', 'logout', 'button')}
              >
                <Logout />
                {t('CMPSR_SIGNOUT', 'Sign out')}
              </StyledLogoutButton>
            </StyledLogoutMenuItem>
          </Box>
        )}
        {userRolesMenuOpen && <UserRoles setUserRolesMenu={setUserRolesMenu} />}
      </MainMenu>
    </>
  )
}
